import React from 'react';
import { Paper, Typography, Button, Box, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Check, X, Pencil } from 'lucide-react';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		backgroundColor: theme.palette.background.paper,
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	details: {
		marginBottom: theme.spacing(1.5),
		'& > div': {
			display: 'flex',
			justifyContent: 'space-between',
			padding: theme.spacing(0.25, 0),
			fontSize: 13,
		},
	},
	actions: {
		display: 'flex',
		gap: theme.spacing(1),
	},
}));

export interface ActionPreview {
	type: 'task_created' | 'subtask_created' | 'message_created' | 'template_created' | 'template_imported' | 'task_imported' | 'task_updated' | 'message_updated' | 'timing_updated' | 'actor_added';
	data: Record<string, unknown>;
}

interface ActionPreviewCardProps {
	preview: ActionPreview;
	onConfirm?: () => void;
	onEdit?: () => void;
	onCancel?: () => void;
	confirmed?: boolean;
}

const TYPE_LABELS: Record<string, string> = {
	task_created: 'New Task',
	subtask_created: 'New Subtask',
	message_created: 'New Message',
	template_created: 'New Template',
	template_imported: 'Imported Template',
	task_imported: 'Imported Task',
	task_updated: 'Task Updated',
	message_updated: 'Message Updated',
	timing_updated: 'Timing Updated',
	actor_added: 'New Actor',
};

export const ActionPreviewCard: React.FC<ActionPreviewCardProps> = ({
	preview,
	onConfirm,
	onEdit,
	onCancel,
	confirmed,
}) => {
	const classes = useStyles();

	const label = TYPE_LABELS[preview.type] ?? 'Action';

	// Extract displayable fields from data (skip internal IDs)
	const displayFields = Object.entries(preview.data).filter(
		([key]) => !key.endsWith('Id') && key !== 'created' && key !== 'updated' && key !== 'imported'
	);

	return (
		<Paper variant="outlined" className={classes.root}>
			<div className={classes.header}>
				<Chip label={label} size="small" color="primary" variant="outlined" />
				{confirmed && <Chip label="Confirmed" size="small" color="primary" />}
			</div>
			<div className={classes.details}>
				{displayFields.map(([key, value]) => (
					<div key={key}>
						<Typography variant="caption" color="textSecondary">
							{key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
						</Typography>
						<Typography variant="caption">{String(value)}</Typography>
					</div>
				))}
			</div>
			{!confirmed && (
				<div className={classes.actions}>
					{onConfirm && (
						<Button size="small" variant="contained" color="primary" startIcon={<Check size={14} />} onClick={onConfirm}>
							Confirm
						</Button>
					)}
					{onEdit && (
						<Button size="small" variant="outlined" startIcon={<Pencil size={14} />} onClick={onEdit}>
							Edit
						</Button>
					)}
					{onCancel && (
						<Button size="small" variant="outlined" color="secondary" startIcon={<X size={14} />} onClick={onCancel}>
							Cancel
						</Button>
					)}
				</div>
			)}
		</Paper>
	);
};
